import { service } from '@ember/service';
import Component from '@glimmer/component';
import { registerDestructor } from '@ember/destroyable';
import StickyLayout from 'tio-ui/components/sticky-layout';
import { Section } from 'tio-ui/components/layout';
import { Button, TooltipButton } from 'tio-ui/components/buttons';
import { Drawer } from 'tio-ui/components/overlays';
import { Modal, Footer as ModalFooter } from 'tio-ui/components/modal';
import stickySidebarAdjustment from 'tio-ui/modifiers/sticky-sidebar-adjustment';
import { AdvancedProgramFilters } from './programs-filters.gts';
import ProgramsTable from './programs-table.gts';
import ProgramsAggregateTable from './programs-aggregate-table.gts';
import SavedReportsDrawer from '../saved-reports-drawer.gts';
import ChartView from '../chart-view.gts';
import ProgramsDataProvider from './programs-data-provider.gts';
import { tracked } from '@glimmer/tracking';
import { eq, gt } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import { on } from '@ember/modifier';
import { fn, hash, get } from '@ember/helper';
import { PhFunnel, PhFloppyDisk, PhTrayArrowDown } from 'ember-phosphor-icons';
import { Checkbox } from 'tio-ui/components/forms';
import { type FieldOption } from '../../../utils/chart-utils.ts';
import {
  OpenSearchFieldMapper,
  type OpenSearchMapping,
} from '../../../utils/opensearch-field-mapper.ts';
import { and } from 'tio-ui/utilities';
import type { NotificationsService } from 'tio-ui/components/notifications';
// import type { ChartType } from '../../../types/chart.ts';
import type CompanyModel from '../../../models/company.ts';
import type ReportModel from '../../../models/reports.ts';
import type { SearchConfig } from '../../../models/reports.ts';
import type Store from '@ember-data/store';
import type TasProgramTemplateModel from '../../../models/tas-program-template.ts';
import type IntlService from 'ember-intl/services/intl';

/**
 * Controller interface - apps provide their own controller that matches this signature
 */
export interface TasProgramsControllerInterface {
  // Pagination
  page: number;
  limit: number;

  // Filter properties
  programTemplateId?: string;
  programCreatedAtStart?: string;
  programCreatedAtEnd?: string;
  excludeNonVoucherPrograms?: boolean;
  showIndividualApplications?: boolean;
  applicationStates?: string;
  applicationCreatedAtStart?: string;
  applicationCreatedAtEnd?: string;
  applicationDateRangePreset?: string;
  includePayments?: boolean;
  paymentStates?: string;
  paymentYear?: string;
  includeCourses?: boolean;
  coursesBeginDate?: string;
  coursesEndDate?: string;
  employeeId?: string;
  terminatedEmployees?: boolean;
  excludeTermVerified?: boolean;
  approverId?: string;
  includeInvoiceDate?: boolean;
  selectedColumns?: string;

  // Method to update properties
  setProperties(updates: Record<string, unknown>): void;
}

export interface ProgramsPageSignature {
  Args: {
    company?: CompanyModel; // Optional for global analytics context
    controller: TasProgramsControllerInterface;

    // Route configuration
    baseRoute: string; // e.g., "authenticated.admin.tuition-assistance" or "authenticated.companies.show.tuition-assistance"
    applicationDetailRoute: string;
    programDetailRoute: string;

    // Optional customizations
    showViewToggle?: boolean; // Whether to show table/chart toggle
    showSaveReports?: boolean; // Whether to show save/load reports functionality
    showColumnSelector?: boolean; // Whether to show the Columns tab in the drawer (default: true)
    filterColumnDependencies?: Record<string, string[]>; // Custom filter-to-column mappings
    isAdminContext?: boolean; // Whether this is being used in an admin context (shows admin-only fields)
    isGlobalContext?: boolean; // Whether this is global analytics (no company filter)
    onCompanyChange?: (company?: CompanyModel) => void; // Optional callback when company selection changes in drawer
  };
  Blocks: {
    breadcrumbs?: [];
    headerActions?: [
      {
        downloadCsv: () => Promise<unknown>;
        currentView: 'table' | 'chart';
        onViewChange: (view: 'table' | 'chart') => void;
        selectedDataset: string;
        onDatasetChange: (dataset: string) => void;
      },
    ];
  };
}

export default class ProgramsPageComponent extends Component<ProgramsPageSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked currentView: 'table' | 'chart' = 'table';
  @tracked currentReport?: ReportModel;
  @tracked columnOrder: string[] = [];
  @tracked selectedChartField = 'tasApplicationStateKeyword';
  @tracked selectedChartType:
    | 'bar'
    | 'line'
    | 'pie'
    | 'donut'
    | 'horizontalBar'
    | 'area'
    | 'summary' = 'bar';
  @tracked selectedDateGrouping: 'day' | 'week' | 'month' | 'year' = 'day';
  @tracked showChartDataTable = false;
  @tracked selectedDataset = 'tas-program-instances';
  @tracked selectedTimeRange = 'all';
  @tracked summaryTitle = '';

  // Sidebar state
  @tracked isExpandedSidebar = true;

  // Drawer state
  @tracked selectedDrawerType: string | null = null;
  @tracked isDrawerOpen = false;
  @tracked activeFilterTab: 'filters' | 'columns' = 'filters';

  // Column removal warning modal
  @tracked showColumnWarningModal = false;
  @tracked columnToRemove: string | null = null;
  @tracked affectedFilters: string[] = [];

  // Responsive media query using tracked property
  @tracked private _isLargeScreen = false;

  constructor(owner: unknown, args: ProgramsPageSignature['Args']) {
    // @ts-expect-error - Owner type mismatch is ok here
    super(owner, args);
    this.setupMediaQuery();
  }

  /***************************/
  /* Media Query Setup      */
  /***************************/

  private setupMediaQuery() {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    this._isLargeScreen = mediaQuery.matches;

    const handleMediaChange = (e: MediaQueryListEvent) => {
      this._isLargeScreen = e.matches;
    };

    mediaQuery.addEventListener('change', handleMediaChange);

    registerDestructor(this, () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    });
  }

  get isLargeScreen() {
    return this._isLargeScreen;
  }

  /***************************/
  /* Program Templates      */
  /***************************/

  programTemplatesData = trackedFunction(this, async (): Promise<TasProgramTemplateModel[]> => {
    const companyId = this.args.company?.id;
    if (!companyId) {
      return [];
    }
    return await this.store.query<TasProgramTemplateModel>('tas-program-template', {
      company_id: companyId,
    });
  });

  get selectedProgramTemplate(): TasProgramTemplateModel | undefined {
    if (!this.args.controller.programTemplateId || !this.programTemplatesData.isFinished) {
      return undefined;
    }
    return this.programTemplatesData.value?.find(
      (template: TasProgramTemplateModel) => template.id === this.args.controller.programTemplateId
    );
  }

  /***************************/
  /* Sidebar Management     */
  /***************************/

  toggleSidebar = () => {
    this.isExpandedSidebar = !this.isExpandedSidebar;
  };

  /***************************/
  /* Drawer Tab Management  */
  /***************************/

  selectFiltersTab = () => {
    this.activeFilterTab = 'filters';
  };

  selectColumnsTab = () => {
    this.activeFilterTab = 'columns';
  };

  /***************************/
  /* Drawer Management      */
  /***************************/

  openDrawer = (type: string) => {
    this.selectedDrawerType = type;
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
    this.selectedDrawerType = null;
  };

  /***************************/
  /* Responsive Drawer Props */
  /***************************/

  get drawerBackdrop() {
    return this.isLargeScreen ? 'none' : 'faded';
  }

  get drawerCloseOnOutsideClick() {
    return !this.isLargeScreen;
  }

  get mainContentClasses() {
    let classes = 'main-content-area';
    if (this.isLargeScreen && this.isDrawerOpen && this.selectedDrawerType) {
      classes += ' drawer-open';
    }
    return classes;
  }

  /***************************/
  /* Selected Filters       */
  /***************************/

  get selectedFilters() {
    return {
      'Program Name': this.selectedProgramTemplate?.programName || 'All',
      'Program Creation Data': this.args.controller.programCreatedAtStart
        ? `From ${this.args.controller.programCreatedAtStart || 'Any'} to ${
            this.args.controller.programCreatedAtEnd || 'Any'
          }`
        : null,
      'Application State': this.args.controller.applicationStates || 'All',
      'Payment State': this.args.controller.paymentStates || 'All',
      'Application Creation Date': this.args.controller.applicationCreatedAtStart
        ? `From ${this.args.controller.applicationCreatedAtStart} to ${
            this.args.controller.applicationCreatedAtEnd || ''
          }`
        : null,
      'Include Courses in the Report': this.args.controller.includeCourses ? 'Yes' : 'No',
      'Include Payments Year': this.args.controller.paymentYear || 'All',
      'Include Courses': this.args.controller.includeCourses ? 'Yes' : 'No',
      'Employee ID Filter': this.args.controller.employeeId || '',
      'Terminated Employees': this.args.controller.terminatedEmployees ? 'Yes' : 'No',
      'Exclude Term Verified': this.args.controller.excludeTermVerified ? 'Yes' : 'No',
      'Approver ID Filter': this.args.controller.approverId || null,
      'Program Template Filter': this.selectedProgramTemplate?.id || null,
      'Include Terminated Employees': this.args.controller.terminatedEmployees ? 'Yes' : null,
      'Include Invoice Dates': this.args.controller.includeInvoiceDate ? 'Yes' : null,
    };
  }

  get hasActiveAdvancedFilters() {
    const controller = this.args.controller;
    return !!(
      controller.showIndividualApplications ||
      controller.applicationStates ||
      controller.applicationCreatedAtStart ||
      controller.applicationCreatedAtEnd ||
      controller.includePayments ||
      controller.paymentStates ||
      controller.paymentYear ||
      controller.includeCourses ||
      controller.coursesBeginDate ||
      controller.coursesEndDate ||
      controller.employeeId ||
      controller.terminatedEmployees ||
      controller.excludeTermVerified ||
      controller.approverId ||
      controller.includeInvoiceDate ||
      controller.excludeNonVoucherPrograms
    );
  }

  /***************************/
  /* Column Selection       */
  /***************************/

  // Fetch OpenSearch mapping dynamically to discover available fields
  mappingData = trackedFunction(this, async (): Promise<FieldOption[]> => {
    try {
      const modelType = 'TasProgramInstance';
      const mapping: OpenSearchMapping = await this.store.adapterFor('search').mapping(modelType);

      const fields = OpenSearchFieldMapper.parseMapping(mapping);

      if (fields.length === 0) {
        throw new Error('No mapping properties found for TasProgramInstance');
      }

      return fields;
    } catch (error) {
      console.error('Error fetching mapping:', error);
      this.notifications.add('Failed to load column mappings from server', {
        appearance: 'error',
      });
      throw error; // Re-throw to let trackedFunction handle error state
    }
  });

  get availableFields(): FieldOption[] {
    // Show error state if mapping failed to load
    if (this.mappingData.error) {
      return [];
    }

    // Show loading state
    if (this.mappingData.isLoading || !this.mappingData.value) {
      return [];
    }

    // Get fields from mapping
    const baseFields = this.mappingData.value;

    // Deprecated fields to exclude from column selector
    // These fields are no longer populated or are being phased out
    // The backend filters these from the mapping endpoint in snake_case,
    // but we check camelCase here for defense-in-depth or cached responses
    const deprecatedFields = new Set([
      'tasApplicationCoursesBeginDate',
      'tasApplicationCoursesEndDate',
      'tasProgramInstanceFieldLockBalanceMultiplierAtStartOfFiscalYearBoolean',
      'paymentFirstDate', // Payment date fields should use paymentLastDate (Payment Date)
      // Add other deprecated fields here as needed
    ]);

    // Filter based on aggregation mode
    // These fields only exist in aggregated responses, not in individual documents
    const aggregateOnlyFields = new Set(['total_applications', 'applications']);
    const paymentFieldPatterns = [/payment/i];

    // Determine if we should show admin-only fields (default to false if not specified)
    const isAdminContext = this.args.isAdminContext ?? false;

    if (this.args.controller.showIndividualApplications) {
      // Individual mode: exclude aggregate-only fields, deprecated fields, and admin-only fields if not in admin context
      return baseFields.filter(
        (field) =>
          !aggregateOnlyFields.has(field.key) &&
          !deprecatedFields.has(field.key) &&
          (isAdminContext || !field.adminOnly) // Filter out admin-only fields if not in admin context
      );
    } else {
      // Aggregate mode: exclude deprecated fields and admin-only fields if not in admin context
      const filteredFields = baseFields.filter(
        (field) =>
          !deprecatedFields.has(field.key) &&
          !paymentFieldPatterns.some((pattern) => pattern.test(field.key)) &&
          (isAdminContext || !field.adminOnly) // Filter out admin-only fields if not in admin context
      );

      // Note: aggregate-only fields like 'total_applications' come from the aggregation response,
      // not from the OpenSearch mapping, so they won't be in the mapping data
      // We need to manually add them if they're not already present
      const hasAggregateFields = filteredFields.some((f) => aggregateOnlyFields.has(f.key));

      if (!hasAggregateFields) {
        return [
          ...filteredFields,
          { key: 'total_applications', label: 'Total Applications', type: 'text', group: 'Other' },
          { key: 'applications', label: 'Applications Count', type: 'text', group: 'Other' },
        ].sort((a, b) => a.label.localeCompare(b.label));
      }

      return filteredFields.sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  get selectedColumnsArray(): string[] {
    const columns = this.args.controller.selectedColumns?.split(',').filter(Boolean) || [];

    if (!this.args.controller.showIndividualApplications) {
      return columns.filter((column) => !/payment/i.test(column));
    }

    return columns;
  }

  setSelectedColumns = (columns: string[]) => {
    this.args.controller.selectedColumns = columns.join(',');
  };

  get selectedColumnsSet() {
    return new Set(this.selectedColumnsArray);
  }

  /***************************/
  /* Filter Management      */
  /***************************/

  updateQueryParameters = (updates: Record<string, unknown>) => {
    this.args.controller.setProperties(updates);
    this.autoEnableColumnsForFilters(updates);
  };

  /**
   * Default filter-to-column dependencies
   * Can be overridden by passing @filterColumnDependencies
   */
  private get filterColumnDependencies(): Record<string, string[]> {
    if (this.args.filterColumnDependencies) {
      return this.args.filterColumnDependencies;
    }

    // Default dependencies (based on tio-admin which has the most up-to-date logic)
    return {
      // Payment filters
      includePayments: [
        'tas_application_payment_paid_amount_integer',
        'tas_application_payment_last_date_date',
      ],
      paymentYear: [
        'tas_application_payment_paid_amount_integer',
        'tas_application_payment_last_date_date',
      ],
      // paymentStates is handled specially in autoEnableColumnsForFilters

      // Course filters
      includeCourses: ['courses'],
      coursesBeginDate: [
        'courses',
        'tas_application_field_courses_begin_date_date',
        'tas_application_field_courses_end_date_date',
      ],
      coursesEndDate: [
        'courses',
        'tas_application_field_courses_begin_date_date',
        'tas_application_field_courses_end_date_date',
      ],

      // Employee filters
      terminatedEmployees: ['employee_termination_date'],
      excludeTermVerified: ['employee_term_verified_date'],
      excludeNonVoucherPrograms: ['employee_date_of_birth', 'student_id'],

      // Application filters
      showIndividualApplications: ['tas_application_id_keyword'],
      applicationStates: ['tas_application_state_keyword'],
      applicationCreatedAtStart: ['tas_application_created_at_date'],
      applicationCreatedAtEnd: ['tas_application_created_at_date'],

      // Program filters
      programCreatedAtStart: ['tas_program_instance_created_at_date'],
      programCreatedAtEnd: ['tas_program_instance_created_at_date'],

      // Other filters
      includeInvoiceDate: ['invoice_date'],
      employeeId: ['employee_name', 'employee_payroll_id_keyword'],
      approverId: ['application_current_reviewers', 'application_possible_reviewers'],
    };
  }

  private getControllerValue = <K extends keyof TasProgramsControllerInterface>(
    key: K
  ): TasProgramsControllerInterface[K] => this.args.controller[key];

  private autoEnableColumnsForFilters(filterUpdates: Record<string, unknown>) {
    const currentColumns = new Set(this.selectedColumnsArray);
    let columnsAdded = false;

    Object.entries(filterUpdates).forEach(([filterKey, filterValue]) => {
      const isFilterActive = this.hasActiveFilterValue(filterValue);

      // Special handling for paymentStates
      if (filterKey === 'paymentStates' && isFilterActive && typeof filterValue === 'string') {
        const selectedStates = filterValue.split(',').filter(Boolean);
        selectedStates.forEach((state) => {
          if (!currentColumns.has(state)) {
            currentColumns.add(state);
            columnsAdded = true;
          }
        });
        return;
      }

      // Standard handling
      if (isFilterActive && this.filterColumnDependencies[filterKey]) {
        const requiredColumns = this.filterColumnDependencies[filterKey];
        requiredColumns.forEach((columnKey: string) => {
          if (!currentColumns.has(columnKey)) {
            currentColumns.add(columnKey);
            columnsAdded = true;
          }
        });
      }
    });

    if (columnsAdded) {
      this.setSelectedColumns(Array.from(currentColumns));
    }
  }

  private hasActiveFilterValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (typeof value === 'boolean') {
      return value === true;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  }

  /***************************/
  /* Column Selection UI    */
  /***************************/

  /**
   * Normalize field keys for comparison by converting to lowercase and removing underscores/dots
   * This allows matching between camelCase (employeeNameKeyword) and snake_case (employee_name_keyword)
   */
  private normalizeFieldKey(key: string): string {
    return key.toLowerCase().replace(/[_.]/g, '');
  }

  /**
   * Check if a field is selected, handling both camelCase and snake_case formats
   */
  isColumnSelected = (fieldKey: string) => {
    const normalizedFieldKey = this.normalizeFieldKey(fieldKey);

    // Check if any selected column matches (in normalized form)
    return Array.from(this.selectedColumnsSet).some(
      (selectedKey) => this.normalizeFieldKey(selectedKey) === normalizedFieldKey
    );
  };

  /**
   * Get list of active filters that depend on a specific column
   */
  private getFiltersRequiringColumn = (columnKey: string): string[] => {
    const normalizedColumnKey = this.normalizeFieldKey(columnKey);
    const affectedFilters: string[] = [];

    Object.entries(this.filterColumnDependencies).forEach(([filterKey, requiredColumns]) => {
      const controllerKey = filterKey as keyof TasProgramsControllerInterface;
      const filterValue = this.getControllerValue(controllerKey);
      if (!this.hasActiveFilterValue(filterValue)) {
        return;
      }

      // Check if the column to remove is required by this filter
      const isRequired = requiredColumns.some(
        (reqCol) => this.normalizeFieldKey(reqCol) === normalizedColumnKey
      );

      if (isRequired) {
        affectedFilters.push(filterKey);
      }
    });

    // Special handling for paymentStates
    const paymentStates = this.args.controller.paymentStates;
    if (paymentStates && typeof paymentStates === 'string') {
      const selectedStates = paymentStates.split(',').filter(Boolean);
      if (selectedStates.some((state) => this.normalizeFieldKey(state) === normalizedColumnKey)) {
        affectedFilters.push('paymentStates');
      }
    }

    return affectedFilters;
  };

  /**
   * Handle column toggle click - checks if warning is needed before toggling
   * Shows modal if column is required by active filters
   */
  handleColumnToggle = (columnKey: string): void => {
    const normalizedNewKey = this.normalizeFieldKey(columnKey);
    const currentColumns = new Set(this.selectedColumnsArray);

    // Find if there's already a column with the same normalized key
    const existingKey = Array.from(currentColumns).find(
      (key) => this.normalizeFieldKey(key) === normalizedNewKey
    );

    // Check if we're trying to remove an existing column (it's currently selected)
    if (existingKey) {
      // Check if this column is required by any active filters
      const requiredByFilters = this.getFiltersRequiringColumn(existingKey);

      if (requiredByFilters.length > 0) {
        // Show warning modal instead of toggling immediately
        this.columnToRemove = existingKey;
        this.affectedFilters = requiredByFilters;
        this.showColumnWarningModal = true;
        return; // Don't toggle - wait for user confirmation
      }
    }

    // Safe to toggle immediately
    this.toggleColumn(columnKey);
  };

  toggleColumn = (columnKey: string) => {
    const normalizedNewKey = this.normalizeFieldKey(columnKey);
    const currentColumns = new Set(this.selectedColumnsArray);

    // Find if there's already a column with the same normalized key
    const existingKey = Array.from(currentColumns).find(
      (key) => this.normalizeFieldKey(key) === normalizedNewKey
    );

    if (existingKey) {
      // Remove the existing key
      currentColumns.delete(existingKey);
    } else {
      // Add the new key
      currentColumns.add(columnKey);
    }

    this.setSelectedColumns(Array.from(currentColumns));
  };

  /**
   * Confirm removal of column and clear affected filters
   */
  confirmColumnRemoval = () => {
    if (!this.columnToRemove) return;

    const currentColumns = new Set(this.selectedColumnsArray);
    currentColumns.delete(this.columnToRemove);
    this.setSelectedColumns(Array.from(currentColumns));

    // Clear affected filters
    const updates: Record<string, unknown> = {};
    this.affectedFilters.forEach((filterKey) => {
      if (filterKey === 'paymentStates') {
        // Remove the specific payment state
        const states = this.args.controller.paymentStates?.split(',').filter(Boolean) || [];
        const normalizedColumnKey = this.normalizeFieldKey(this.columnToRemove!);
        const remainingStates = states.filter(
          (state) => this.normalizeFieldKey(state) !== normalizedColumnKey
        );
        updates.paymentStates = remainingStates.join(',');
      } else {
        // Clear the filter
        updates[filterKey] = null;
      }
    });

    if (Object.keys(updates).length > 0) {
      this.updateQueryParameters(updates);
    }

    this.cancelColumnRemoval();
  };

  /**
   * Cancel column removal
   */
  cancelColumnRemoval = () => {
    this.showColumnWarningModal = false;
    this.columnToRemove = null;
    this.affectedFilters = [];
  };

  selectAllColumns = () => {
    this.setSelectedColumns(this.availableFields.map((f) => f.key));
  };

  deselectAllColumns = () => {
    this.setSelectedColumns([]);
  };

  get groupedFields(): Array<[string, FieldOption[]]> {
    const fields = this.availableFields;
    const groups = new Map<string, FieldOption[]>();

    fields.forEach((field) => {
      const group = field.group || 'Other';
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      groups.get(group)!.push(field);
    });

    // Convert to sorted array of [groupName, fields] tuples
    const groupedArray = Array.from(groups.entries());

    // Custom sort order for groups
    // Admin Configuration is always at the bottom (after Other)
    const order = [
      'Employee',
      'Program Instance',
      'Program Details',
      'Program Template',
      'Application',
      'Courses',
      'Payments',
      'Company',
      'Other',
      'Admin Configuration', // Admin fields always at the bottom
    ];

    return groupedArray.sort((a, b) => {
      const aIndex = order.indexOf(a[0]);
      const bIndex = order.indexOf(b[0]);
      // Groups not in the order list go to position 998 (before Admin Configuration)
      const aPosition = aIndex === -1 ? 998 : aIndex;
      const bPosition = bIndex === -1 ? 998 : bIndex;
      return aPosition - bPosition;
    });
  }

  /***************************/
  /* Report Management      */
  /***************************/

  get reportSearchConfig() {
    // For chart reports, use chart-specific filters and settings
    if (this.currentView === 'chart') {
      return {
        viewType: this.currentView,
        filters: {
          // Chart queries are based on field selection and time ranges, not table filters
          selectedField: this.selectedChartField,
          selectedDataset: this.selectedDataset,
          selectedTimeRange: this.selectedTimeRange,
        },
        chartSettings: {
          selectedField: this.selectedChartField,
          selectedType: this.selectedChartType,
          summaryTitle: this.summaryTitle,
          dateGrouping: this.selectedDateGrouping,
          showDataTable: this.showChartDataTable,
          selectedDataset: this.selectedDataset,
          selectedTimeRange: this.selectedTimeRange,
        },
      };
    } else {
      // For table reports, use the full filter set
      return {
        viewType: this.currentView,
        filters: {
          programTemplateId: this.args.controller.programTemplateId,
          programCreatedAtStart: this.args.controller.programCreatedAtStart,
          programCreatedAtEnd: this.args.controller.programCreatedAtEnd,
          excludeNonVoucherPrograms: this.args.controller.excludeNonVoucherPrograms,
          applicationStates: this.args.controller.applicationStates?.split(',').filter(Boolean),
          applicationCreatedAtStart: this.args.controller.applicationCreatedAtStart,
          applicationCreatedAtEnd: this.args.controller.applicationCreatedAtEnd,
          paymentStates: this.args.controller.paymentStates?.split(',').filter(Boolean),
          paymentYear: this.args.controller.paymentYear,
          includeCourses: this.args.controller.includeCourses,
          coursesBeginDate: this.args.controller.coursesBeginDate,
          coursesEndDate: this.args.controller.coursesEndDate,
          employeeId: this.args.controller.employeeId,
          terminatedEmployees: this.args.controller.terminatedEmployees,
          excludeTermVerified: this.args.controller.excludeTermVerified,
          approverId: this.args.controller.approverId,
        },
        tableSettings: {
          showIndividualApplications: this.args.controller.showIndividualApplications,
          selectedColumns: this.args.controller.selectedColumns,
          columnOrder: this.columnOrder,
        },
      };
    }
  }

  handleLoadReport = (report: ReportModel) => {
    // Set the view based on the report type first
    this.currentView = report.isChartBuilder ? 'chart' : 'table';

    if (report.searchConfig) {
      const config: SearchConfig = report.searchConfig;

      if (this.currentView === 'chart') {
        // NOTE: Chart values are already set by ReportLoader via context methods!
        // DO NOT reset them here or we'll lose the loaded values.
        // The code below is kept for reference but commented out.

        /*
        // For chart reports, only load chart-specific settings
        // Reset chart settings to defaults first
        this.selectedChartField = 'tasApplicationStateKeyword';
        this.selectedChartType = 'bar';
        this.summaryTitle = '';
        this.selectedDateGrouping = 'day';
        this.showChartDataTable = false;
        this.selectedDataset = 'tas-program-instances';
        this.selectedTimeRange = 'all';

        // Apply chart settings from loaded configuration
        if (config.chartSettings) {
          if (config.chartSettings.selectedField) {
            this.selectedChartField = config.chartSettings.selectedField;
          }
          if (config.chartSettings.selectedType) {
            this.selectedChartType = config.chartSettings.selectedType;
          }
          if (config.chartSettings.summaryTitle) {
            this.summaryTitle = config.chartSettings.summaryTitle;
          }
          if (config.chartSettings.dateGrouping) {
            this.selectedDateGrouping = config.chartSettings.dateGrouping;
          }
          if (config.chartSettings.showDataTable !== undefined) {
            this.showChartDataTable = config.chartSettings.showDataTable;
          }
          if (config.chartSettings.selectedDataset) {
            this.selectedDataset = config.chartSettings.selectedDataset;
          }
          if (config.chartSettings.selectedTimeRange) {
            this.selectedTimeRange = config.chartSettings.selectedTimeRange;
          }
        }

        // Apply chart filters if they exist in the old format
        if (config.filters) {
          if (config.filters.selectedField) {
            this.selectedChartField = config.filters.selectedField;
          }
          if (config.filters.selectedDataset) {
            this.selectedDataset = config.filters.selectedDataset;
          }
          if (config.filters.selectedTimeRange) {
            this.selectedTimeRange = config.filters.selectedTimeRange;
          }
        }
        */

        // For chart reports, don't apply table filters - keep them at defaults
        const defaultTableState: Record<string, unknown> = {
          page: this.args.controller.page,
          limit: this.args.controller.limit,
          programTemplateId: '',
          programCreatedAtStart: '',
          programCreatedAtEnd: '',
          excludeNonVoucherPrograms: false,
          showIndividualApplications: false,
          applicationStates: '',
          applicationCreatedAtStart: '',
          applicationCreatedAtEnd: '',
          includePayments: false,
          paymentStates: '',
          paymentYear: '',
          includeCourses: false,
          coursesBeginDate: '',
          coursesEndDate: '',
          employeeId: '',
          terminatedEmployees: false,
          excludeTermVerified: false,
          approverId: '',
          includeInvoiceDate: false,
        };

        this.args.controller.setProperties(defaultTableState);
      } else {
        // For table reports, use the existing table filter loading logic
        // Reset column order to default - will be overridden if saved report has one
        this.columnOrder = [];

        const defaultState: Record<string, unknown> = {
          page: this.args.controller.page,
          limit: this.args.controller.limit,

          // Program filters
          programTemplateId: '',
          programCreatedAtStart: '',
          programCreatedAtEnd: '',
          excludeNonVoucherPrograms: false,

          // Application filters
          showIndividualApplications: false,
          applicationStates: '', // Empty string - when split by comma and filtered becomes []
          applicationCreatedAtStart: '',
          applicationCreatedAtEnd: '',

          // Payment filters
          includePayments: false,
          paymentStates: '', // Empty string for comma-separated values
          paymentYear: '',

          // Course filters
          includeCourses: false,
          coursesBeginDate: '',
          coursesEndDate: '',

          // Employee filters
          employeeId: '',
          terminatedEmployees: false,
          excludeTermVerified: false,
          approverId: '',

          // Invoice filters
          includeInvoiceDate: false,
        };

        const savedValues: Record<string, unknown> = {};

        // Extract saved values from the loaded configuration using object destructuring
        if (config.filters) {
          const filters = config.filters as Record<string, unknown>;

          const programTemplateId = filters.programTemplateId as string | undefined;
          const programCreatedAtStart = filters.programCreatedAtStart as string | undefined;
          const programCreatedAtEnd = filters.programCreatedAtEnd as string | undefined;
          const excludeNonVoucherPrograms = filters.excludeNonVoucherPrograms as
            | boolean
            | undefined;
          const applicationStates = filters.applicationStates as string | string[] | undefined;
          const applicationCreatedAtStart = filters.applicationCreatedAtStart as string | undefined;
          const applicationCreatedAtEnd = filters.applicationCreatedAtEnd as string | undefined;
          const paymentStates = filters.paymentStates as string | string[] | undefined;
          const paymentYear = filters.paymentYear as string | undefined;
          const includeCourses = filters.includeCourses as boolean | undefined;
          const coursesBeginDate = filters.coursesBeginDate as string | undefined;
          const coursesEndDate = filters.coursesEndDate as string | undefined;
          const employeeId = filters.employeeId as string | undefined;
          const terminatedEmployees = filters.terminatedEmployees as boolean | undefined;
          const excludeTermVerified = filters.excludeTermVerified as boolean | undefined;
          const approverId = filters.approverId as string | undefined;

          // Build savedValues object with only defined values
          Object.assign(savedValues, {
            ...(programTemplateId !== undefined && { programTemplateId }),
            ...(programCreatedAtStart !== undefined && { programCreatedAtStart }),
            ...(programCreatedAtEnd !== undefined && { programCreatedAtEnd }),
            ...(excludeNonVoucherPrograms !== undefined && { excludeNonVoucherPrograms }),
            ...(applicationStates !== undefined && {
              applicationStates: Array.isArray(applicationStates)
                ? applicationStates.join(',')
                : applicationStates,
            }),
            ...(applicationCreatedAtStart !== undefined && { applicationCreatedAtStart }),
            ...(applicationCreatedAtEnd !== undefined && { applicationCreatedAtEnd }),
            ...(paymentStates !== undefined && {
              paymentStates: Array.isArray(paymentStates) ? paymentStates.join(',') : paymentStates,
            }),
            ...(paymentYear !== undefined && { paymentYear }),
            ...(includeCourses !== undefined && { includeCourses }),
            ...(coursesBeginDate !== undefined && { coursesBeginDate }),
            ...(coursesEndDate !== undefined && { coursesEndDate }),
            ...(employeeId !== undefined && { employeeId }),
            ...(terminatedEmployees !== undefined && { terminatedEmployees }),
            ...(excludeTermVerified !== undefined && { excludeTermVerified }),
            ...(approverId !== undefined && { approverId }),
          });
        }

        // Apply table settings
        if (config.tableSettings?.showIndividualApplications !== undefined) {
          savedValues.showIndividualApplications = config.tableSettings.showIndividualApplications;
        }
        if (config.tableSettings?.selectedColumns) {
          savedValues.selectedColumns = config.tableSettings.selectedColumns;
        }
        if (config.tableSettings?.columnOrder) {
          this.columnOrder = config.tableSettings.columnOrder;
        }

        // Merge default state with saved values (saved values override defaults)
        const finalState = { ...defaultState, ...savedValues };

        // Auto-enable includePayments if paymentStates has values
        if (finalState.paymentStates && String(finalState.paymentStates).trim() !== '') {
          finalState.includePayments = true;
        }

        // Auto-enable includeCourses if course date filters have values
        if (
          (finalState.coursesBeginDate && String(finalState.coursesBeginDate).trim() !== '') ||
          (finalState.coursesEndDate && String(finalState.coursesEndDate).trim() !== '')
        ) {
          finalState.includeCourses = true;
        }

        // Apply the complete state in one operation
        this.args.controller.setProperties(finalState);
      }
    } else {
      // No config - reset to clean state based on view type
      if (this.currentView === 'chart') {
        // Reset chart settings
        this.selectedChartField = 'tasApplicationStateKeyword';
        this.selectedChartType = 'bar';
        this.selectedDateGrouping = 'day';
        this.showChartDataTable = false;
        this.selectedDataset = 'tas-program-instances';
        this.selectedTimeRange = 'all';
      }

      // Always reset table filters to default state
      const defaultState: Record<string, unknown> = {
        page: this.args.controller.page,
        limit: this.args.controller.limit,
        programTemplateId: '',
        programCreatedAtStart: '',
        programCreatedAtEnd: '',
        excludeNonVoucherPrograms: false,
        showIndividualApplications: false,
        applicationStates: '',
        applicationCreatedAtStart: '',
        applicationCreatedAtEnd: '',
        includePayments: false,
        paymentStates: '',
        paymentYear: '',
        includeCourses: false,
        coursesBeginDate: '',
        coursesEndDate: '',
        employeeId: '',
        terminatedEmployees: false,
        excludeTermVerified: false,
        approverId: '',
        includeInvoiceDate: false,
      };

      this.args.controller.setProperties(defaultState);
    }
  };

  handleViewChange = (view: 'table' | 'chart') => {
    this.currentView = view;
    // Clear current report when switching views since this represents a new report type
    this.currentReport = undefined;
  };

  handleDatasetChange = (dataset: string) => {
    this.selectedDataset = dataset;
    // Reset to first page when changing datasets
    this.args.controller.setProperties({ page: 1 });
  };

  setCurrentReport = (report: ReportModel) => {
    this.currentReport = report;
  };

  /***************************/
  /* Chart Management       */
  /***************************/

  setChartField = (field: string) => {
    this.selectedChartField = field;
  };

  setChartType = (
    type: 'bar' | 'line' | 'pie' | 'donut' | 'horizontalBar' | 'area' | 'summary'
  ) => {
    this.selectedChartType = type;
  };

  setChartDateGrouping = (dateGrouping: 'day' | 'week' | 'month' | 'year') => {
    this.selectedDateGrouping = dateGrouping;
  };

  setChartShowDataTable = (show: boolean) => {
    this.showChartDataTable = show;
  };

  setChartDataset = (dataset: string) => {
    this.selectedDataset = dataset;
  };

  setChartTimeRange = (timeRange: string) => {
    this.selectedTimeRange = timeRange;
  };

  setSummaryTitle = (title: string) => {
    this.summaryTitle = title;
  };

  /**
   * Handle column order changes from the table
   * Stores the new order for saving in reports
   */
  handleColumnOrderChange = (newOrder: string[]) => {
    this.columnOrder = newOrder;
    console.log('Column order updated:', newOrder);
  };

  <template>
    <ProgramsDataProvider
      @company={{@company}}
      @selectedDataset={{this.selectedDataset}}
      @programTemplateId={{@controller.programTemplateId}}
      @programCreatedAtStart={{@controller.programCreatedAtStart}}
      @programCreatedAtEnd={{@controller.programCreatedAtEnd}}
      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
      @showIndividualApplications={{@controller.showIndividualApplications}}
      @applicationStates={{@controller.applicationStates}}
      @applicationCreatedAtStart={{@controller.applicationCreatedAtStart}}
      @applicationCreatedAtEnd={{@controller.applicationCreatedAtEnd}}
      @includePayments={{@controller.includePayments}}
      @paymentStates={{@controller.paymentStates}}
      @paymentYear={{@controller.paymentYear}}
      @includeCourses={{@controller.includeCourses}}
      @coursesBeginDate={{@controller.coursesBeginDate}}
      @coursesEndDate={{@controller.coursesEndDate}}
      @employeeId={{@controller.employeeId}}
      @terminatedEmployees={{@controller.terminatedEmployees}}
      @excludeTermVerified={{@controller.excludeTermVerified}}
      @approverId={{@controller.approverId}}
      @includeInvoiceDate={{@controller.includeInvoiceDate}}
      @selectedColumns={{this.selectedColumnsArray}}
      @page={{@controller.page}}
      @limit={{@controller.limit}}
      @updateQueryParameters={{this.updateQueryParameters}}
      as |provider|
    >
      <StickyLayout
        @sidebarWidth="w-16"
        @sidebarSticky={{true}}
        class={{this.mainContentClasses}}
        {{stickySidebarAdjustment}}
      >
        <:header>
          <Section>
            <:header>
              {{yield to="breadcrumbs"}}
            </:header>
            <:actions>
              {{yield
                (hash
                  downloadCsv=provider.downloadCsv
                  currentView=this.currentView
                  onViewChange=this.handleViewChange
                  selectedDataset=this.selectedDataset
                  onDatasetChange=this.handleDatasetChange
                )
                to="headerActions"
              }}
            </:actions>
          </Section>
        </:header>

        <:main>
          {{#if (eq this.currentView "table")}}
            {{#if provider.isLoading}}
              <div class="flex items-center justify-center p-8">
                <div class="text-gray-500 dark:text-gray-400">Loading table data...</div>
              </div>
            {{else}}
              <Section class="overflow-x-auto m-6">
                <:body>
                  {{#if @controller.showIndividualApplications}}
                    <ProgramsTable
                      @results={{provider.data}}
                      @isLoading={{provider.isLoading}}
                      @selectedColumns={{this.selectedColumnsArray}}
                      @columnOrder={{this.columnOrder}}
                      @showIndividualApplications={{@controller.showIndividualApplications}}
                      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
                      @includeCourses={{@controller.includeCourses}}
                      @includeInvoiceDate={{@controller.includeInvoiceDate}}
                      @terminatedEmployees={{@controller.terminatedEmployees}}
                      @excludeTermVerified={{@controller.excludeTermVerified}}
                      @applicationDetailRoute={{@applicationDetailRoute}}
                      @programDetailRoute={{@programDetailRoute}}
                      @paymentStates={{@controller.paymentStates}}
                      @limit={{@controller.limit}}
                      @page={{@controller.page}}
                      @selectedFilters={{this.selectedFilters}}
                      @onPageChange={{fn (mut @controller.page)}}
                      @onColumnOrderChange={{this.handleColumnOrderChange}}
                    />
                  {{else}}
                    <ProgramsAggregateTable
                      @results={{provider.data}}
                      @isLoading={{provider.isLoading}}
                      @limit={{@controller.limit}}
                      @page={{@controller.page}}
                      @selectedColumns={{this.selectedColumnsArray}}
                      @availableFields={{this.availableFields}}
                      @selectedFilters={{this.selectedFilters}}
                      @onPageChange={{fn (mut @controller.page)}}
                    />
                  {{/if}}
                </:body>
              </Section>
            {{/if}}
          {{else}}
            {{! Chart view }}
            <Section class="m-6">
              <:header>
                📊 Chart View -
                {{provider.totalRecords}}
                records ({{provider.executionTime}}ms)
              </:header>
              <:body>
                {{#if provider.isLoading}}
                  <div class="flex items-center justify-center p-8">
                    <div class="text-gray-500 dark:text-gray-400">Loading chart data...</div>
                  </div>
                {{else}}
                  <ChartView
                    @selectedField={{this.selectedChartField}}
                    @chartType={{this.selectedChartType}}
                    @dateGrouping={{this.selectedDateGrouping}}
                    @showDataTable={{this.showChartDataTable}}
                    @selectedDataset={{this.selectedDataset}}
                    @selectedTimeRange={{this.selectedTimeRange}}
                    @summaryTitle={{this.summaryTitle}}
                    @onFieldChange={{this.setChartField}}
                    @onChartTypeChange={{this.setChartType}}
                    @onDateGroupingChange={{this.setChartDateGrouping}}
                    @onShowDataTableChange={{this.setChartShowDataTable}}
                    @onDatasetChange={{this.setChartDataset}}
                    @onTimeRangeChange={{this.setChartTimeRange}}
                    @onSummaryTitleChange={{this.setSummaryTitle}}
                  />
                {{/if}}
              </:body>
            </Section>
          {{/if}}
        </:main>

        <:sidebar>
          <TooltipButton
            @tooltip="Advanced Filters"
            @tooltipPlacement="left"
            @appearance="minimal"
            @intent="default"
            @size="sm"
            @class="!p-1 !rounded-md relative"
            @tooltipDisabled={{this.isDrawerOpen}}
            data-tour="advanced-filters-button"
            {{on "click" (fn this.openDrawer "advanced-filters")}}
          >
            <PhFunnel @size={{24}} class="text-gray-600" />
            {{#if this.hasActiveAdvancedFilters}}
              <span
                class="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-900"
                aria-label="Active filters"
              ></span>
            {{/if}}
          </TooltipButton>

          {{#if @showSaveReports}}
            <TooltipButton
              @tooltip="Load & Save Report"
              @tooltipPlacement="left"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              @icon={{PhFloppyDisk}}
              @class="!p-1 !rounded-md"
              @tooltipDisabled={{this.isDrawerOpen}}
              {{on "click" (fn this.openDrawer "load-save")}}
            />
          {{/if}}

          <TooltipButton
            @tooltip="Download CSV"
            @tooltipPlacement="left"
            @appearance="minimal"
            @intent="default"
            @size="sm"
            @icon={{PhTrayArrowDown}}
            @class="!p-1 !rounded-md"
            @tooltipDisabled={{this.isDrawerOpen}}
            {{on "click" provider.downloadCsv}}
          />
        </:sidebar>
      </StickyLayout>

      {{! Drawer for Advanced Filters and Load/Save }}
      <div class="drawer-container {{if this.isDrawerOpen 'drawer-open' 'drawer-closed'}}">
        <Drawer
          @isOpen={{this.isDrawerOpen}}
          @onClose={{this.closeDrawer}}
          @placement="right"
          @size="lg"
          @target="#tio-drawer-target"
          @closeOnOutsideClick={{this.drawerCloseOnOutsideClick}}
          class="flex flex-col"
          as |drawer|
        >
          {{#if (eq this.selectedDrawerType "advanced-filters")}}
            <drawer.Header>Advanced Options</drawer.Header>
            <drawer.Body class="flex-1 overflow-y-auto">
              {{#if @showColumnSelector}}
                <div class="border-b border-gray-200 mb-4">
                  <nav class="-mb-px flex space-x-8 px-4" aria-label="Tabs">
                    <button
                      type="button"
                      class="{{if
                          (eq this.activeFilterTab 'filters')
                          'border-blue-500 text-blue-600'
                          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }}
                        whitespace-nowrap py-3 px-2 border-b-2 font-medium text-sm"
                      {{on "click" this.selectFiltersTab}}
                    >
                      Filters
                    </button>
                    <button
                      type="button"
                      class="{{if
                          (eq this.activeFilterTab 'columns')
                          'border-blue-500 text-blue-600'
                          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }}
                        whitespace-nowrap py-3 px-2 border-b-2 font-medium text-sm"
                      {{on "click" this.selectColumnsTab}}
                    >
                      Columns
                    </button>
                  </nav>
                </div>
              {{/if}}

              {{#if (eq this.activeFilterTab "filters")}}
                <div class="{{if @showColumnSelector 'p-4' 'pt-0 p-4'}}">
                  <AdvancedProgramFilters
                    @company={{@company}}
                    @isAdminContext={{@isAdminContext}}
                    @onCompanyChange={{@onCompanyChange}}
                    @programTemplateId={{@controller.programTemplateId}}
                    @programCreatedAtStart={{@controller.programCreatedAtStart}}
                    @programCreatedAtEnd={{@controller.programCreatedAtEnd}}
                    @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
                    @showIndividualApplications={{@controller.showIndividualApplications}}
                    @applicationStates={{@controller.applicationStates}}
                    @applicationCreatedAtStart={{@controller.applicationCreatedAtStart}}
                    @applicationCreatedAtEnd={{@controller.applicationCreatedAtEnd}}
                    @applicationDateRangePreset={{@controller.applicationDateRangePreset}}
                    @includePayments={{@controller.includePayments}}
                    @paymentStates={{@controller.paymentStates}}
                    @paymentYear={{@controller.paymentYear}}
                    @includeCourses={{@controller.includeCourses}}
                    @coursesBeginDate={{@controller.coursesBeginDate}}
                    @coursesEndDate={{@controller.coursesEndDate}}
                    @employeeId={{@controller.employeeId}}
                    @terminatedEmployees={{@controller.terminatedEmployees}}
                    @excludeTermVerified={{@controller.excludeTermVerified}}
                    @approverId={{@controller.approverId}}
                    @includeInvoiceDate={{@controller.includeInvoiceDate}}
                    @onFilterUpdate={{this.updateQueryParameters}}
                  />
                </div>
              {{else if (and @showColumnSelector (eq this.activeFilterTab "columns"))}}
                <div class="p-4">
                  <div class="mb-4">
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      {{on "click" this.selectAllColumns}}
                      class="mr-2"
                    >
                      Select All
                    </Button>
                    <Button
                      @intent="default"
                      @appearance="outlined"
                      {{on "click" this.deselectAllColumns}}
                    >
                      Deselect All
                    </Button>
                  </div>

                  {{#if this.mappingData.isLoading}}
                    <div class="p-4 text-center text-gray-500 dark:text-gray-400">
                      <p class="text-sm">Loading available columns...</p>
                    </div>
                  {{else}}
                    <div class="space-y-4">
                      {{#each this.groupedFields as |groupData|}}
                        {{#let (get groupData "0") (get groupData "1") as |groupName fields|}}
                          <div class="space-y-2">
                            <h4
                              class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-2"
                            >{{groupName}}</h4>
                            {{#each fields as |field|}}
                              <div class="flex items-center pl-2">
                                <Checkbox
                                  @checked={{this.isColumnSelected field.key}}
                                  @onChange={{fn this.handleColumnToggle field.key}}
                                  class="mr-2"
                                />
                                <label class="text-sm font-medium">{{field.label}}</label>
                              </div>
                            {{/each}}
                          </div>
                        {{/let}}
                      {{/each}}
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </drawer.Body>
          {{else if (eq this.selectedDrawerType "load-save")}}
            <drawer.Header>Load & Save Report</drawer.Header>
            <drawer.Body class="p-4 mb-6 overflow-y-auto">
              <SavedReportsDrawer
                @onLoad={{this.handleLoadReport}}
                @onExport={{provider.downloadCsv}}
                @searchData={{this.reportSearchConfig}}
                @reportType={{if (eq this.currentView "chart") "chart_builder" "table_builder"}}
                @currentReport={{this.currentReport}}
                @updateQueryParameters={{this.updateQueryParameters}}
                @setCurrentReport={{this.setCurrentReport}}
                @setDataset={{this.setChartDataset}}
                @setGroupByField={{this.setChartField}}
                @setChartType={{this.setChartType}}
                @setTimeRange={{this.setChartTimeRange}}
                @setDateGrouping={{this.setChartDateGrouping}}
                @setShowTableView={{this.setChartShowDataTable}}
              />
            </drawer.Body>
          {{/if}}
        </Drawer>
      </div>

      {{! Column Removal Warning Modal }}
      <Modal
        @isOpen={{this.showColumnWarningModal}}
        @onClose={{this.cancelColumnRemoval}}
        @size="sm"
        as |m|
      >
        <m.Header>Cannot Remove Column</m.Header>
        <m.Body>
          <p class="text-sm text-gray-700 dark:text-gray-300 mb-4">
            This column is required by the following active filter{{if
              (gt this.affectedFilters.length 1)
              "s"
            }}:
          </p>
          <ul class="list-disc list-inside space-y-1 mb-4">
            {{#each this.affectedFilters as |filterKey|}}
              <li class="text-sm font-medium text-gray-900 dark:text-gray-100">
                {{filterKey}}
              </li>
            {{/each}}
          </ul>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Removing this column will also clear the associated filter{{if
              (gt this.affectedFilters.length 1)
              "s"
            }}. Do you want to proceed?
          </p>
        </m.Body>
        <m.Footer>
          <ModalFooter
            @submitText="Remove Column"
            @onSubmit={{this.confirmColumnRemoval}}
            @onCancel={{this.cancelColumnRemoval}}
            @cancelText="Cancel"
            @submitIntent="danger"
          />
        </m.Footer>
      </Modal>
    </ProgramsDataProvider>
  </template>
}
